import { useState } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring } from "framer-motion";
import { Globe2, Handshake, ArrowLeftRight, Ship, Network, DoorOpen, ArrowUpRight } from "lucide-react";
import { Eyebrow, Reveal } from "./Reveal";
import { SERVICES } from "./data";

const ICONS = [Globe2, Handshake, ArrowLeftRight, Ship, Network, DoorOpen];

const Row = ({ s, i, onEnter }) => {
  const Icon = ICONS[i];
  return (
    <Reveal delay={i * 0.05}>
      <div onMouseEnter={() => onEnter(i)} className="service-row group relative grid grid-cols-12 items-center gap-4 border-t border-navy/12 py-8 sm:py-10" data-testid={`service-row-${i}`}>
        <span className="col-span-2 font-mono text-sm text-slate-500 sm:col-span-1">0{i + 1}</span>
        <h3 className="col-span-10 font-display text-2xl font-bold tracking-tight text-navy transition-transform duration-500 group-hover:translate-x-3 sm:col-span-5 sm:text-4xl">
          {s.title}
        </h3>
        <p className="col-span-12 text-sm leading-relaxed text-slate-600 sm:col-span-5 md:text-base">{s.text}</p>
        <span className="col-span-1 hidden justify-self-end sm:block">
          <span className="grid h-12 w-12 place-items-center rounded-full border border-navy/15 text-navy transition-colors duration-500 group-hover:border-copper group-hover:bg-copper group-hover:text-white">
            <Icon className="h-5 w-5 group-hover:hidden" />
            <ArrowUpRight className="hidden h-5 w-5 group-hover:block" />
          </span>
        </span>
        <span className="absolute bottom-0 left-0 h-px w-0 bg-copper transition-[width] duration-700 group-hover:w-full" />
      </div>
    </Reveal>
  );
};

const Preview = ({ x, y, active }) => (
  <motion.div style={{ x, y }} className="pointer-events-none absolute left-0 top-0 z-20 hidden lg:block">
    <AnimatePresence>
      {active !== null && (
        <motion.div
          key="preview"
          initial={{ opacity: 0, scale: 0.6, rotate: -6 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          exit={{ opacity: 0, scale: 0.6 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="relative -ml-[140px] -mt-[100px] h-[200px] w-[280px] overflow-hidden rounded-[20px] shadow-2xl ring-1 ring-gold-light/40"
        >
          {SERVICES.map((s, i) => (
            <img key={s.title} src={s.img} alt="" className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${active === i ? "opacity-100" : "opacity-0"}`} />
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

export const Services = () => {
  const [active, setActive] = useState(null);
  const x = useSpring(useMotionValue(0), { stiffness: 150, damping: 20 });
  const y = useSpring(useMotionValue(0), { stiffness: 150, damping: 20 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    x.set(e.clientX - r.left);
    y.set(e.clientY - r.top);
  };
  return (
  <section id="services" className="bg-mist py-24 sm:py-32" data-testid="services-section">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <div className="mb-16 grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Eyebrow>What we do</Eyebrow>
          <Reveal>
            <h2 className="mt-6 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-navy sm:text-6xl">
              Six ways we keep <em className="font-serif font-normal italic text-copper">metal</em> moving.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="self-end lg:col-span-4 lg:col-start-9">
          <p className="text-base leading-relaxed text-slate-600 md:text-lg">From the first enquiry to the final shipment, we act as the trusted link between international supply and demand.</p>
        </Reveal>
      </div>
      <div className="relative border-b border-navy/12" onMouseMove={onMove} onMouseLeave={() => setActive(null)}>
        <Preview x={x} y={y} active={active} />
        {SERVICES.map((s, i) => <Row key={s.title} s={s} i={i} onEnter={setActive} />)}
      </div>
    </div>
  </section>
  );
};
